import { useApolloClient } from '@apollo/react-hooks'
import type { Theme } from '@material-ui/core'
import { CircularProgress, makeStyles } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import type {
  Contact,
  ContactBySourceQuery,
  ContactQuery,
  SearchContactDealsQuery,
  SearchContactOpportunitiesQuery,
  SearchContactsWithIntegrationQuery
} from '@paintscout/api'
import {
  ContactBySourceDocument,
  ContactDocument,
  SearchContactDealsDocument,
  SearchContactOpportunitiesDocument,
  useSearchContactsWithIntegrationLazyQuery
} from '@paintscout/api'
import { hasIntegrationInterface, uuid } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import {
  useDebouncedCallback,
  Button,
  ConflictResolveDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogStackContext,
  DialogTitle,
  Grid,
  InputField,
  SelectContactDetailsDialog,
  SelectDealDialog,
  SelectOpportunityDialog,
  Tab,
  Tabs,
  Typography,
  useClientOptions,
  useUser
} from '@paintscout/ui'
import type { DialogProps, PLDDeal, SalesforceOpportunity } from '@paintscout/ui'

const getContactSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'pipeline_deals':
      return 'Pipeline CRM'
    case 'service_titan':
      return 'Service Titan'
    case 'salesforce':
      return 'Salesforce'
    default:
      return 'Unknown Integration'
  }
}
import { jsonToLucene } from 'json-to-lucene'
import omit from 'lodash/omit'
import { useSnackbar } from 'notistack'
import React, { useContext, useEffect, useRef, useState } from 'react'
import ContactResultsList from './ContactResultsList'

export interface ContactSearchDialogProps extends DialogProps {
  onCreateNew?: (query: string) => any
  onConfirm?: (contact: Contact | Partial<Contact>, currentDealId?: number | string, providerName?: string) => any
  onCancel?: (event: React.MouseEvent<HTMLElement>) => any
  ExtraContent?: JSX.Element
}

export const useStyles = makeStyles<Theme, { hasSearchIntegration: boolean }>((theme: Theme) => ({
  root: {},
  content: {},
  results: {
    height: ({ hasSearchIntegration }) => (hasSearchIntegration ? '450px' : '500px'),
    [theme.breakpoints.down('xs')]: {
      height: '90%',
      overflowY: 'auto',
      overflowX: 'none'
    }
  },
  tabs: {
    paddingLeft: 0,
    paddingBottom: theme.spacing(),
    [theme.breakpoints.down('xs')]: {
      '& button': {
        marginRight: theme.spacing(2)
      }
    }
  },
  noResults: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    ...theme.typography.overline,
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  searchAdornment: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  newButton: {
    height: '100%'
  },
  paddingTop: {
    paddingTop: ({ hasSearchIntegration }) => (hasSearchIntegration ? 0 : theme.spacing(1))
  }
}))

function ContactSearchDialog({
  onConfirm,
  onCancel,
  onCreateNew: _onCreateNew,
  ExtraContent,
  ...props
}: ContactSearchDialogProps) {
  const { options } = useClientOptions()
  const { preferences } = useUser()
  const hasPld = !!options.options.pipelineKey?.value && !!options.options?.pipelineKey?.enabled
  const hasServiceTitan = hasIntegrationInterface({ preferences, options, integration: 'serviceTitan' })
  // const hasSalesforce = hasIntegrationInterface({ preferences, options, integration: 'salesforce' })
  // const hasSalesforceSandbox = hasIntegrationInterface({ preferences, options, integration: 'salesforcesandbox' })
  const hasSalesforce = false
  const hasSalesforceSandbox = false
  const hasSearchIntegration = hasPld || hasServiceTitan || hasSalesforce || hasSalesforceSandbox

  const classes = useStyles({ hasSearchIntegration })
  const { enqueueSnackbar } = useSnackbar()
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const onCreateNew = options.options.quotes.forceIntegrationContacts ? null : _onCreateNew
  const [selectedIntegration, setSelectedIntegration] = useState(
    hasPld
      ? 'pipeline_deals'
      : hasServiceTitan
        ? 'service_titan_customers'
        : hasSalesforce
          ? 'salesforce_contacts'
          : hasSalesforceSandbox
            ? 'salesforcesandbox_contacts'
            : 'paintscout'
  )

  const bookmark = useRef<string>(undefined)
  const [query, setQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const fetchingContact = useRef<boolean>(false)
  const sort = '-updated_date'
  const apolloClient = useApolloClient()

  const [search, { loading, data, error, fetchMore }] = useSearchContactsWithIntegrationLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d: SearchContactsWithIntegrationQuery) => {
      const newBookmark = d?.paintscout?.bookmark

      if (newBookmark && newBookmark !== bookmark.current) {
        bookmark.current = newBookmark
      }
    }
  })

  // Handle load more for this lazy search on scroll, but debounce it
  const loadMoreHandler = () => {
    // only fetch more results for paintscout results
    if (!loading && selectedIntegration === 'paintscout' && hasMore) {
      fetchMore({
        variables: {
          paintscoutQuery: jsonToLucene({ query }) || '*:*',
          integrationQuery: '',
          useIntegration: false,
          integration: 'paintscout',
          bookmark: bookmark.current,
          sort
        },
        updateQuery: (previousResult: SearchContactsWithIntegrationQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.paintscout) {
            return previousResult
          }

          return {
            ...previousResult,
            ...fetchMoreResult,
            paintscout: {
              ...previousResult.paintscout,
              ...fetchMoreResult.paintscout,
              bookmark: fetchMoreResult.paintscout.bookmark,
              contacts: [...previousResult.paintscout.contacts, ...fetchMoreResult.paintscout.contacts]
            }
          }
        }
      })
    }
  }
  // Query and fetchMore debouncers
  const debouncedQuery = useDebouncedCallback(search, 250)
  const handleLoadMore = useDebouncedCallback(loadMoreHandler, 250)

  // On text change re-query
  useEffect(() => {
    if ((query.trim() || selectedIntegration === 'service_titan_customers') && debouncedQuery) {
      debouncedQuery({
        variables: {
          paintscoutQuery: jsonToLucene({ query }),
          integrationQuery: query,
          useIntegration: selectedIntegration !== 'paintscout',
          integration: selectedIntegration,
          sort
        }
      })
    }
  }, [debouncedQuery, query, selectedIntegration])

  useEffect(() => {
    if (error) {
      console.error(error)
      enqueueSnackbar('Unable to search Contacts', { variant: 'error' })
    }
  }, [error])

  const completeContactSelect = async ({
    fullContact,
    dealIds
  }: {
    fullContact: Contact
    dealIds?: Contact['pldDeals']
  }) => {
    console.log({ fullContact })
    if (dealIds.length > 0 && fullContact.source_id) {
      const deals: PLDDeal[] = await apolloClient
        .query<SearchContactDealsQuery>({
          query: SearchContactDealsDocument,
          variables: { contactSourceId: fullContact.source_id },
          fetchPolicy: 'no-cache'
        })
        .then((res) => res.data?.searchContactDeals?.results ?? [])

      setIsOpen(false)
      await handleSelectDeal(deals, fullContact)
    } else if (fullContact.source === 'salesforce' && fullContact.source_id) {
      console.log({ selectedIntegration })
      const opportunites: SalesforceOpportunity[] = await apolloClient
        .query<SearchContactOpportunitiesQuery>({
          query: SearchContactOpportunitiesDocument,
          variables: {
            contactSourceId: fullContact.source_id,
            integration: selectedIntegration.split('_')?.[0]
          },
          fetchPolicy: 'no-cache'
        })
        .then((res) => {
          console.log(res)
          return res.data?.searchContactOpportunities?.results ?? []
        })

      setIsOpen(false)
      await handleSelectOpportunity(opportunites, fullContact)
    } else if (fullContact.source === 'service_titan') {
      setIsOpen(false)
      await handleSelectContactDetails(omit(fullContact, 'company'))
    } else {
      const selectedDealId = dealIds[0]?.id ?? null
      return onConfirm(fullContact, selectedDealId)
    }
  }

  const handleSelectContact = async (
    selectedContact: (Contact & { dealId?: number }) | (Partial<Contact> & { dealId?: number })
  ) => {
    const id = (selectedContact as Partial<Contact>)._id
    // fetch existing contact by source
    if ((selectedContact.source_id || selectedContact.source_user_id) && !id) {
      fetchingContact.current = true
      const contact = await apolloClient
        .query<ContactBySourceQuery>({
          query: ContactBySourceDocument,
          // we do not want to cache this or else we might cache the "null" response
          // for when it is created for the first time
          fetchPolicy: 'no-cache',
          variables: {
            source: selectedContact.source,
            source_id: `${selectedContact.source_id}`
          }
        })
        .then((res) => {
          return res.data.contactBySource as Contact
        })
        .catch((error) => {
          Sentry.captureException(error, { tags: { contactId: id } })
          return null
        })
      fetchingContact.current = false

      const dealIds: Contact['pldDeals'] = selectedContact?.pldDeals ?? []
      if (contact && selectedContact) {
        // If we already have the contact but the selected one has been updated,
        // check if we want to update on copy with new details.
        const updatedFields: string[] = checkContactUpdated({ existing: contact, current: selectedContact })
        if (updatedFields.length > 0) {
          const updates: string[] = updatedFields.map(
            (field) =>
              `${fieldNameTranslate[field]} was changed from "${contact[field]}" to "${selectedContact[field]}"`
          )
          openDialog(ConflictResolveDialog, {
            title: 'Merge Contact Updates',
            message: `The selected contact has been updated in ${getContactSourceDisplayName(contact.source)}. Do you want to update the existing contact with the new details?`,
            updates,
            yesLabel: 'Yes',
            noLabel: 'No',
            scary: true,
            onConfirm: async () => {
              const fullContact = {
                ...contact,
                ...updatedFields.reduce((acc, field) => {
                  acc[field] = selectedContact[field]
                  return acc
                }, {})
              }
              dismissDialog()
              await completeContactSelect({ fullContact, dealIds })
            },
            onCancel: async () => {
              dismissDialog()
              await completeContactSelect({ fullContact: contact, dealIds })
            }
          })
        } else {
          dismissDialog()
          await completeContactSelect({ fullContact: contact, dealIds })
        }
      } else {
        const fullContact = contact ? contact : { _id: uuid(), ...selectedContact }
        await completeContactSelect({ fullContact, dealIds })
      }
    } else {
      // fetch normal contact
      try {
        if (id) {
          fetchingContact.current = true
          const contact = await apolloClient
            .query<ContactQuery>({
              query: ContactDocument,
              fetchPolicy: 'no-cache',
              variables: {
                id
              }
            })
            .then((res) => res.data.contact)

          fetchingContact.current = false
          return onConfirm(contact)
        }
        return onConfirm({ _id: uuid(), ...selectedContact })
      } catch (e) {
        console.error(e)
        enqueueSnackbar('An error occurred while getting this Contact', { variant: 'error' })
      }
    }
  }

  const handleSelectOpportunity = async (opportunities: Contact['salesforceOpportunities'][], contact: Contact) => {
    openDialog(
      SelectOpportunityDialog,
      {
        opportunities,
        onConfirm: (opportunityId: string) => {
          dismissDialog()
          if (opportunityId === 'none') {
            return onConfirm(contact)
          }
          return onConfirm(contact, opportunityId)
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: true }
    )
  }

  const handleSelectDeal = async (deals: PLDDeal[], contact: Contact) => {
    openDialog(
      SelectDealDialog,
      {
        deals,
        contact,
        onConfirm: (dealId: string) => {
          dismissDialog()
          if (dealId === 'none') {
            return onConfirm(contact)
          }
          return onConfirm(contact, Number(dealId))
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: false }
    )
  }

  const handleSelectContactDetails = async (contact: Contact) => {
    openDialog(
      SelectContactDetailsDialog,
      {
        contactId: contact.source_user_id,
        onConfirm: (values: Record<string, string>) => {
          dismissAllDialogs()
          return onConfirm({ ...contact, ...values }, Number(contact.source_id), 'serviceTitan')
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: false }
    )
  }

  const paintscoutContacts = data?.paintscout?.contacts ?? []

  const integrationContacts = data?.integration?.results ?? []

  const contacts = selectedIntegration !== 'paintscout' ? integrationContacts : paintscoutContacts
  const numResults = (selectedIntegration === 'paintscout' && data?.paintscout?.total_rows) ?? contacts.length
  const hasMore = contacts.length < numResults

  const allowPSContacts = !options.options.quotes.forceIntegrationContacts

  const inputRef = useRef<HTMLDivElement | null>(null)

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="md" {...props}>
      <DialogTitle loading={fetchingContact.current}>Select a Contact</DialogTitle>
      <DialogContent>
        <>
          {ExtraContent}
          {hasSearchIntegration && (
            <Tabs
              classes={{ root: classes.tabs }}
              value={selectedIntegration}
              onChange={(ev, value) => {
                setSelectedIntegration(value)
                bookmark.current = undefined
              }}
            >
              {allowPSContacts && <Tab label="PaintScout" value="paintscout" disabled={fetchingContact.current} />}
              {hasPld && <Tab label="Pipeline CRM" value="pipeline_deals" disabled={fetchingContact.current} />}
              {hasServiceTitan && (
                <Tab label="Service Titan" value="service_titan_customers" disabled={fetchingContact.current} />
              )}
              {hasSalesforce && (
                <Tab label="Salesforce" value="salesforce_contacts" disabled={fetchingContact.current} />
              )}
              {hasSalesforceSandbox && (
                <Tab
                  label="Salesforce (Sandbox)"
                  value="salesforcesandbox_contacts"
                  disabled={fetchingContact.current}
                />
              )}
            </Tabs>
          )}

          <Grid container spacing={2} alignItems="stretch" className={classes.paddingTop}>
            <Grid item style={{ flex: '1 1 auto' }}>
              <InputField
                id="query"
                inputRef={inputRef}
                autoFocus={true}
                data-testid="contact-search-input"
                disabled={fetchingContact.current}
                endAdornment={
                  numResults > 0 && (
                    <Typography variant="subtitle1">
                      {numResults} result{numResults > 1 ? 's' : ''}
                    </Typography>
                  )
                }
                startAdornment={
                  <div className={classes.searchAdornment}>
                    {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  </div>
                }
                placeholder="Search existing contacts"
                fullWidth={true}
                defaultValue={''}
                onChange={(ev) => {
                  setQuery(ev.currentTarget.value)
                  setShowResults(true)
                }}
                onFocus={() => {
                  if (query.trim()) setShowResults(true)
                }}
              />
            </Grid>
            {onCreateNew && (
              <Grid item style={{ flex: '0 1 auto' }}>
                <Button
                  className={classes.newButton}
                  icon={AddIcon}
                  onClick={() => {
                    onCreateNew(query)
                  }}
                  disabled={fetchingContact.current}
                >
                  New
                </Button>
              </Grid>
            )}
          </Grid>

          <div className={classes.results}>
            {contacts.length > 0 && showResults && (
              <ContactResultsList
                results={contacts}
                disabled={fetchingContact.current}
                onClickAway={(event) => {
                  const target = event.target as Node
                  if (inputRef.current?.contains(target)) return
                  setShowResults(false)
                }}
                onContactClick={handleSelectContact}
                onLoadMore={() => {
                  fetchingContact.current = true
                  handleLoadMore()
                  fetchingContact.current = false
                }}
              />
            )}
            {data && !loading && contacts.length === 0 && <div className={classes.noResults}>No results</div>}
          </div>
        </>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      />
    </Dialog>
  )
}

const fieldNameTranslate = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone_number: 'Phone Number',
  address: 'Address',
  country: 'Country',
  city: 'City',
  postal: 'Postal',
  company: 'Company'
}

function checkContactUpdated({ existing, current }: { existing: Contact; current: Partial<Contact> }): string[] {
  const updatedFields: string[] = []
  const fieldsToCheck = [
    'first_name',
    'last_name',
    'email',
    'phone_number',
    'address',
    'country',
    'city',
    'postal',
    'company'
  ]
  fieldsToCheck.forEach((field) => {
    if (current[field] && existing[field] && existing[field] !== current[field]) {
      updatedFields.push(field)
    }
  })
  return updatedFields
}

export default ContactSearchDialog
